import { Box, Container, Button, ButtonGroup, Grid, Paper, Skeleton, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { setFilterKeyword, setFilterLoadedKeyword, setFilterLoadedPlatform, setFilterMinDatePicker, setAdvancedView } from "src/context/reducers/filter-settings/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback, useRef } from "react";
import { fDate } from "src/utils/format-time";
import DataGridCustomKeywords from "src/components/data-grid/data-grid-custom-keywords";
import { _mock } from "src/_mock";
import trafficDetailsApi, { useGetCountriesQuery, useGetDevicesQuery, useGetKeywordsQuery, useGetPlatformsStatsQuery, useGetStatsQuery } from "src/context/api/trafficDetails/api";
import Iconify from "src/components/iconify";
import { useGetPlatformsQuery, useGetStatsDashCustomQuery } from "src/context/api/trafficAnalysis/api";
import { differenceInDays } from "date-fns";
import BankingWidgetSummary from "./banking-widget-summary-toogle";
import { SplashScreen } from "src/components/loading-screen";
import DataGridCustomDay from "src/components/data-grid/data-grid-custom-day";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import ChartLine from "src/sections/charts/chart-line";
import DataGridCustomDevices from "src/components/data-grid/data-grid-custom-devices";
import DataGridCustomCountries from "src/components/data-grid/data-grid-custom-countries";
import { enqueueSnackbar } from "notistack";
import { useLocales } from "src/locales";
import { usePreviousLocation } from "src/components/previousLocationContext";
import DataGridCustomPlatforms from "src/components/data-grid/data-grid-custom-platforms";
import { Icon } from '@iconify/react';
import {
    GridFilterModel,
    GridLogicOperator,
    GridSortModel
} from '@mui/x-data-grid-pro';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

declare global {
    interface Window {
        crossbrandNoLimit?: boolean;
    }
}

export default function trafficDetails() {
    const { t, currentLang } = useLocales();
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);
    const theme = useTheme();
    const workspace = useCurrentWorkspace();
    const [kpiToogle, setKPIToogle]: any = useState([
        "brandSearches",
        "totalBrandTraffic"
    ]);

    const [pinnedMetrics] = useState([
        "brandSearches",
        "totalBrandTraffic",
        "organicBrandTraffic",
        "adBrandTraffic",
        "blendedCtr",
        "blendedCpc",
        "adCost"
    ]);

    const [visibleStats, setVisibleStats] = useState([
        "brandSearches",
        "totalBrandTraffic",
        "organicBrandTraffic",
        "adBrandTraffic",
        "blendedCtr",
        "blendedCpc",
        "adCost"
    ]);
    const [value, setValue]: any = useState(0);
    const prevLocation = usePreviousLocation();
    const isAdvancedView = useSelector((state: any) => state.filters.isAdvancedView);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const handlePaginationModelChange = (newModel: any) => {
        setPaginationModel(newModel);
    };
    // Add state to track which tabs have been loaded
    const [loadedTabs, setLoadedTabs] = useState<number[]>([0]);
    // Track which pages we've already preloaded
    const [preloadedPages, setPreloadedPages] = useState<number[]>([0]);
    // Add state for stats menu
    const [statsMenuAnchorEl, setStatsMenuAnchorEl] = useState<null | HTMLElement>(null);
    const statsMenuOpen = Boolean(statsMenuAnchorEl);
    const [statsMenuSearch, setStatsMenuSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (prevLocation === "/dash/competitor-focus" || prevLocation === "/dash/competitors-monitoring") {
            dispatch(setFilterLoadedKeyword([]));
            dispatch(setFilterKeyword([]))
        }
    }, [prevLocation])

    enum ColorSchema {
        PRIMARY = 'primary',
        SECONDARY = 'secondary',
        TERTIARY = 'tertiary',
        DARKER = 'darker',
        ERROR = 'error',
        WARNING = 'warning',
        INFO = 'info',
        SUCCESS = 'success',
        DANGER = 'danger',
        DEFAULT = 'default',
        CPC = 'cpc',
        BLENDED_CPC = 'blendedCpc',
        BRAND_SEARCH = 'brandSearch',
        BRAND_TRAFFIC = 'brandTraffic',
        ORGANIC_BRAND_TRAFFIC = 'organicBrandTraffic',
        PAID_BRAND_TRAFFIC = 'paidBrandTraffic',
        BLENDED_CTR = 'blendedCtr',
        AD_COST = 'adCost',
        SEARCH_BRAND_TRAFFIC = 'searchBrandTraffic',
        SHOPPING_BRAND_TRAFFIC = 'shoppingBrandTraffic',
        PMAX_BRAND_TRAFFIC = 'pmaxBrandTraffic',
        ORGANIC_CTR = 'organicCtr',
        PAID_CTR = 'paidCtr',
        SHOPPING_PAID_CTR = 'shoppingPaidCtr',
        SEARCH_PAID_CTR = 'searchPaidCtr',
        PMAX_PAID_CTR = 'pmaxPaidCtr',
        SEARCH_AD_COST = 'searchAdCost',
        SHOPPING_AD_COST = 'shoppingAdCost',
        PMAX_AD_COST = 'pmaxAdCost',
        SEARCH_CPC = 'searchCpc',
        SHOPPING_CPC = 'shoppingCpc',
        PMAX_CPC = 'pmaxCpc',
    }

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    const { data: platformsData, error: platformsDataError, isFetching: isFetchingPlatformsData } = useGetPlatformsStatsQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        countries: filterStore.countries,
        device: filterStore?.devices,
        keywords: filterStore.keywords,
        platform: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery || !loadedTabs.includes(4),
        refetchOnMountOrArgChange: false,
    });

    const { data: platforms, error: platformsError, isFetching: isFetchingPlatforms } = useGetPlatformsQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
    },
        { skip: skipQuery }
    );

    useEffect(() => {
        if (platforms)
            dispatch(setFilterLoadedPlatform(platforms))
    }, [platforms])


    useEffect(() => {
        if (location?.pathname?.split("/")[3] === "countries") {
            setValue(1)
        }
    }, [location])

    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
        const stored = localStorage.getItem("dataGridFilters");
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch {
                return { items: [], logicOperator: 'and' };
            }
        }
        return { items: [], logicOperator: 'and' };
    });



    const getDaysDifference = (startDate: any, endDate: any) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return differenceInDays(end, start);
    }

    const { data: data } = useGetStatsDashCustomQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore?.timestep,
        device: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
        platform: filterStore?.platforms
    }, {
        skip: skipQuery,
        refetchOnMountOrArgChange: false,
    });

    const [customLabels, setCustomLabels] = useState<any>(data?.stats?.map((item: any) => item[filterStore?.timestep]));

    const fieldMapping: { [key: string]: string } = {
        'brandSearches': 'total_organic_searches',
        'brandTraffic': 'total_brand_traffic',
        'organicBrandTraffic': 'total_organic_clicks',
        'paidBrandTraffic': 'total_ad_clicks',
    };

    const { data: keywordsData, error: keywordsError, isFetching: isFetchingKeywords } = useGetKeywordsQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        device: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
        platform: filterStore?.platforms,
        page: paginationModel?.page ? paginationModel.page + 1 : 1,
        pageSize: paginationModel?.pageSize || 10,
        sortField: sortModel[0]?.field ? fieldMapping[sortModel[0].field] || sortModel[0].field : 'total_organic_searches',
        sortOrder: sortModel[0]?.sort || 'desc',
        filters: filterModel
    }, {
        skip: skipQuery,
        refetchOnMountOrArgChange: false,
    });

    const totalRows = keywordsData?.pagination?.total_rows ?? 0;

    const dataKeywords = keywordsData?.keywords?.map((item: any, index: any) => ({
        id: index,
        keyword: item?.search_term || '',
        brandSearches: item?.total_organic_searches?.toFixed(0) || 0,
        brandSearchesCompare: item?.total_organic_searches_compare?.toFixed(0) || 0,
        brandSearchesDiff: item?.total_organic_searches_diff?.toFixed(0) || 0,
        brandTraffic: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0),
        brandTrafficCompare: (item?.total_ad_clicks_compare + item?.total_organic_clicks_compare).toFixed(0),
        brandTrafficDiff: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0) - (item?.total_ad_clicks_compare + item?.total_organic_clicks_compare).toFixed(0),
        organicBrandTraffic: item?.total_organic_clicks?.toFixed(0) || 0,
        organicBrandTrafficCompare: item?.total_organic_clicks_compare?.toFixed(0) || 0,
        organicBrandTrafficDiff: item?.total_organic_clicks_diff?.toFixed(0) || 0,
        paidBrandTraffic: item?.total_ad_clicks?.toFixed(0) || 0,
        paidBrandTrafficCompare: item?.total_ad_clicks_compare?.toFixed(0) || 0,
        paidBrandTrafficDiff: item?.total_ad_clicks_diff?.toFixed(0) || 0,
        uncapturedBrandTraffic: item?.uncaptured_brand_traffic?.toFixed(0) || 0,
        uncapturedBrandTrafficCompare: item?.uncaptured_brand_traffic_compare?.toFixed(0) || 0,
        uncapturedBrandTrafficDiff: item?.uncaptured_brand_traffic_diff?.toFixed(0) || 0,
        blendedCtr: item?.blended_ctr || 0,
        blendedCtrCompare: item?.blended_ctr_compare || 0,
        blendedCtrDiff: item?.blended_ctr_diff?.toFixed(2) || 0,
        paidCtr: ((item.total_ad_clicks / item.total_organic_searches) * 100).toFixed(2),
        paidCtrCompare: item?.total_ad_clicks_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        paidCtrDiff: ((item.total_ad_clicks / item.total_organic_searches) * 100 - (item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) || 0,
        organicCtr: item?.total_organic_clicks && item?.total_organic_searches
            ? ((item.total_organic_clicks / item.total_organic_searches) * 100).toFixed(2) : 0,
        organicCtrCompare: item?.total_organic_clicks_compare && item?.total_organic_searches_compare
            ? ((item.total_organic_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        organicCtrDiff: ((item.total_organic_clicks / item.total_organic_searches - item.total_organic_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) || 0,
        shoppingCtr: item?.total_ad_clicks_shopping && item?.total_organic_searches
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches) * 100).toFixed(2) : 0,
        shoppingCtrCompare: item?.total_ad_clicks_shopping_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        shoppingCtrDiff: item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches - item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtr: item?.total_ad_clicks_pmax && item?.total_organic_searches
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches) * 100).toFixed(2) : 0,
        pmaxCtrCompare: item?.total_ad_clicks_pmax_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtrDiff: item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches - item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtr: item?.total_ad_clicks_search && item?.total_organic_searches
            ? ((item.total_ad_clicks_search / item.total_organic_searches) * 100).toFixed(2) : 0,
        searchCtrCompare: item?.total_ad_clicks_search_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtrDiff: item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search / item.total_organic_searches - item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        uncapturedPercent: item?.uncaptured_brand_traffic && item?.total_organic_searches
            ? ((item.uncaptured_brand_traffic / item.total_organic_searches) * 100).toFixed(2) : 0,
        uncapturedPercentCompare: item?.uncaptured_brand_traffic_compare && item?.total_organic_searches_compare
            ? ((item.uncaptured_brand_traffic_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        uncapturedPercentDiff: item?.uncaptured_brand_traffic && item?.uncaptured_brand_traffic_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.uncaptured_brand_traffic / item.total_organic_searches - item.uncaptured_brand_traffic_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        adCost: item?.total_ad_cost ? item.total_ad_cost.toFixed(0) : 0,
        adCostCompare: item?.total_ad_cost_compare ? item.total_ad_cost_compare.toFixed(0) : 0,
        adCostDiff: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        adCostShopping: item?.total_ad_cost_shopping ? item.total_ad_cost_shopping.toFixed(0) : 0,
        adCostShoppingCompare: item?.total_ad_cost_shopping_compare ? item.total_ad_cost_shopping_compare.toFixed(0) : 0,
        adCostShoppingDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare
            ? (item.total_ad_cost_shopping - item.total_ad_cost_shopping_compare).toFixed(0) : 0,
        adCostPmax: item?.total_ad_cost_pmax ? item.total_ad_cost_pmax.toFixed(0) : 0,
        adCostPmaxCompare: item?.total_ad_cost_pmax_compare ? item.total_ad_cost_pmax_compare.toFixed(0) : 0,
        adCostPmaxDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare
            ? (item.total_ad_cost_pmax - item.total_ad_cost_pmax_compare).toFixed(0) : 0,
        adCostSearch: item?.total_ad_cost_search ? item.total_ad_cost_search.toFixed(0) : 0,
        adCostSearchCompare: item?.total_ad_cost_search_compare ? item.total_ad_cost_search_compare.toFixed(0) : 0,
        adCostSearchDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare
            ? (item.total_ad_cost_search - item.total_ad_cost_search_compare).toFixed(0) : 0,
        blendedCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks)).toFixed(2) : 0,
        blendedCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) : 0,
        blendedCpcDiff: (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks) - item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) || 0,
        paidCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / item.total_ad_clicks).toFixed(2) : 0,
        paidCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) : 0,
        paidCpcDiff: (item.total_ad_cost / item.total_ad_clicks - item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) || 0,
        paidSearchCpc: item?.total_ad_cost_search && item?.total_ad_clicks_search
            ? (item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2) : 0,
        paidSearchCpcCompare: item?.total_ad_cost_search_compare && item?.total_ad_clicks_search_compare
            ? (item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2) : 0,
        paidSearchCpcDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare && item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (parseFloat((item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2))) - (parseFloat((item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2))) : 0,
        paidShoppingCpc: item?.total_ad_cost_shopping && item?.total_ad_clicks_shopping
            ? (item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2) : 0,
        paidShoppingCpcCompare: item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2) : 0,
        paidShoppingCpcDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (parseFloat((item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2))) - (parseFloat((item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2))) : 0,
        paidPmaxCpc: item?.total_ad_cost_pmax && item?.total_ad_clicks_pmax
            ? (item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2) : 0,
        paidPmaxCpcCompare: item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2) : 0,
        paidPmaxCpcDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (parseFloat((item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2))) - (parseFloat((item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2))) : 0,
        sparkline: item?.sparkline || [],
        sparkline_compare: item?.sparkline_compare || [],
        granularity: item?.granularity || "date"
    })) || [];

    const { data: devicesData, error: deviceError, isFetching: isFetchingDevice } = useGetDevicesQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        countries: filterStore.countries,
        device: filterStore?.devices,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
        platform: filterStore?.platforms,
    }, {
        skip: skipQuery || !loadedTabs.includes(2),
        refetchOnMountOrArgChange: false,
    });

    const dataPlatforms = platformsData?.platforms?.map((item: any, index: any) => ({
        id: index,
        platform: item?.platform?.charAt(0).toUpperCase() + item?.platform?.slice(1).toLowerCase(),
        brandSearches: item?.total_organic_searches.toFixed(0) || 0,
        brandSearchesCompare: item?.total_organic_searches_compare?.toFixed(0) || 0,
        brandSearchesDiff: (item?.total_organic_searches - item?.total_organic_searches_compare).toFixed(0) || 0,
        brandTraffic: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0) || 0,
        brandTrafficCompare: (item?.total_ad_clicks_compare + item?.total_organic_clicks_compare).toFixed(0) || 0,
        brandTrafficDiff: (
            ((item?.total_ad_clicks || 0) + (item?.total_organic_clicks || 0)) -
            ((item?.total_ad_clicks_compare || 0) + (item?.total_organic_clicks_compare || 0))
        ).toFixed(0),
        organicBrandTraffic: item?.total_organic_clicks?.toFixed(0) || 0,
        organicBrandTrafficCompare: item?.total_organic_clicks_compare?.toFixed(0) || 0,
        organicBrandTrafficDiff: (item?.total_organic_clicks - item?.total_organic_clicks_compare)?.toFixed(0) || 0,
        paidBrandTraffic: item?.total_ad_clicks?.toFixed(0) || 0,
        paidBrandTrafficCompare: item?.total_ad_clicks_compare?.toFixed(0) || 0,
        paidBrandTrafficDiff: (item?.total_ad_clicks - item?.total_ad_clicks_compare)?.toFixed(0) || 0,
        uncapturedBrandTraffic: item?.uncaptured_brand_traffic?.toFixed(0) || 0,
        uncapturedBrandTrafficCompare: item?.uncaptured_brand_traffic_compare?.toFixed(0) || 0,
        uncapturedBrandTrafficDiff: (item?.uncaptured_brand_traffic - item?.uncaptured_brand_traffic_compare)?.toFixed(0) || 0,
        blendedCtr: item?.blended_ctr || 0,
        blendedCtrCompare: item?.blended_ctr_compare || 0,
        blendedCtrDiff: (Math.min(item?.blended_ctr, 100) - Math.min(item?.blended_ctr_compare, 100)).toFixed(2) || 0,
        paidCtr: item?.total_ad_clicks && item?.total_organic_searches
            ? ((item.total_ad_clicks / item.total_organic_searches) * 100).toFixed(2) : 0,
        paidCtrCompare: item?.total_ad_clicks_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        paidCtrDiff: (Math.min((item.total_ad_clicks / item.total_organic_searches) * 100, 100) - Math.min((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100, 100))?.toFixed(2),
        organicCtr: ((item?.total_organic_clicks / item?.total_organic_searches) * 100) || 0,
        organicCtrCompare: ((item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100) || 0,
        organicCtrDiff: ((item?.total_organic_clicks / item?.total_organic_searches - item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        uncapturedPercent: ((item?.uncaptured_brand_traffic / item?.total_organic_searches) * 100) || 0,
        uncapturedPercentCompare: ((item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100) || 0,
        uncapturedPercentDiff: ((item?.uncaptured_brand_traffic / item?.total_organic_searches - item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        adCost: item?.total_ad_cost?.toFixed(0) || 0,
        adCostShopping: item?.total_ad_cost_shopping ? item.total_ad_cost_shopping.toFixed(0) : 0,
        adCostShoppingCompare: item?.total_ad_cost_shopping_compare ? item.total_ad_cost_shopping_compare.toFixed(0) : 0,
        adCostShoppingDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare
            ? (item.total_ad_cost_shopping - item.total_ad_cost_shopping_compare).toFixed(0) : 0,
        adCostPmax: item?.total_ad_cost_pmax ? item.total_ad_cost_pmax.toFixed(0) : 0,
        adCostPmaxCompare: item?.total_ad_cost_pmax_compare ? item.total_ad_cost_pmax_compare.toFixed(0) : 0,
        adCostPmaxDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare
            ? (item.total_ad_cost_pmax - item.total_ad_cost_pmax_compare).toFixed(0) : 0,
        adCostSearch: item?.total_ad_cost_search ? item.total_ad_cost_search.toFixed(0) : 0,
        adCostSearchCompare: item?.total_ad_cost_search_compare ? item.total_ad_cost_search_compare.toFixed(0) : 0,
        adCostSearchDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare
            ? (item.total_ad_cost_search - item.total_ad_cost_search_compare).toFixed(0) : 0,
        adCostCompare: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        adCostDiff: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        blendedCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks)).toFixed(2) : 0,
        blendedCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) : 0,
        blendedCpcDiff: (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks) - item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) || 0,
        paidCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / item.total_ad_clicks).toFixed(2) : 0,
        paidCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) : 0,
        paidCpcDiff: item?.total_ad_cost && item?.total_ad_cost_compare && item?.total_ad_clicks && item?.total_ad_clicks_compare
            ? (parseFloat((item.total_ad_cost / item.total_ad_clicks).toFixed(2)) - parseFloat((item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2))) : 0,
        paidSearchCpc: item?.total_ad_cost_search && item?.total_ad_clicks_search
            ? (item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2) : 0,
        paidSearchCpcCompare: item?.total_ad_cost_search_compare && item?.total_ad_clicks_search_compare
            ? (item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2) : 0,
        paidSearchCpcDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare && item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (parseFloat((item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2))) - (parseFloat((item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2))) : 0,
        paidShoppingCpc: item?.total_ad_cost_shopping && item?.total_ad_clicks_shopping
            ? (item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2) : 0,
        paidShoppingCpcCompare: item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2) : 0,
        paidShoppingCpcDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (parseFloat((item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2))) - (parseFloat((item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2))) : 0,
        paidPmaxCpc: item?.total_ad_cost_pmax && item?.total_ad_clicks_pmax
            ? (item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2) : 0,
        paidPmaxCpcCompare: item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2) : 0,
        paidPmaxCpcDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (parseFloat((item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2))) - (parseFloat((item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2))) : 0,
        sparkline: item?.sparkline,
        sparkline_compare: item?.sparkline_compare,
        granularity: item?.granularity || "date"
    })) || [];

    const dataDevices = devicesData?.devices?.map((item: any, index: any) => ({
        id: index,
        device: item?.device?.charAt(0).toUpperCase() + item?.device?.slice(1).toLowerCase(),
        brandSearches: item?.total_organic_searches.toFixed(0) || 0,
        brandSearchesCompare: item?.total_organic_searches_compare?.toFixed(0) || 0,
        brandSearchesDiff: (item?.total_organic_searches - item?.total_organic_searches_compare).toFixed(0) || 0,
        brandTraffic: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0) || 0,
        brandTrafficCompare: (item?.total_ad_clicks_compare + item?.total_organic_clicks_compare).toFixed(0) || 0,
        brandTrafficDiff: (
            ((item?.total_ad_clicks || 0) + (item?.total_organic_clicks || 0)) -
            ((item?.total_ad_clicks_compare || 0) + (item?.total_organic_clicks_compare || 0))
        ).toFixed(0),
        organicBrandTraffic: item?.total_organic_clicks?.toFixed(0) || 0,
        organicBrandTrafficCompare: item?.total_organic_clicks_compare?.toFixed(0) || 0,
        organicBrandTrafficDiff: (item?.total_organic_clicks - item?.total_organic_clicks_compare)?.toFixed(0) || 0,
        paidBrandTraffic: item?.total_ad_clicks?.toFixed(0) || 0,
        paidBrandTrafficCompare: item?.total_ad_clicks_compare?.toFixed(0) || 0,
        paidBrandTrafficDiff: (item?.total_ad_clicks - item?.total_ad_clicks_compare)?.toFixed(0) || 0,
        uncapturedBrandTraffic: item?.uncaptured_brand_traffic?.toFixed(0) || 0,
        uncapturedBrandTrafficCompare: item?.uncaptured_brand_traffic_compare?.toFixed(0) || 0,
        uncapturedBrandTrafficDiff: (item?.uncaptured_brand_traffic - item?.uncaptured_brand_traffic_compare)?.toFixed(0) || 0,
        blendedCtr: item?.blended_ctr || 0,
        blendedCtrCompare: item?.blended_ctr_compare || 0,
        blendedCtrDiff: (Math.min(item?.blended_ctr, 100) - Math.min(item?.blended_ctr_compare, 100)).toFixed(2) || 0,
        paidCtr: item?.total_ad_clicks && item?.total_organic_searches
            ? ((item.total_ad_clicks / item.total_organic_searches) * 100).toFixed(2) : 0,
        paidCtrCompare: item?.total_ad_clicks_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        paidCtrDiff: (Math.min((item.total_ad_clicks / item.total_organic_searches) * 100, 100) - Math.min((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100, 100))?.toFixed(2),
        shoppingCtr: item?.total_ad_clicks_shopping && item?.total_organic_searches
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches) * 100).toFixed(2) : 0,
        shoppingCtrCompare: item?.total_ad_clicks_shopping_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        shoppingCtrDiff: item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches - item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtr: item?.total_ad_clicks_pmax && item?.total_organic_searches
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches) * 100).toFixed(2) : 0,
        pmaxCtrCompare: item?.total_ad_clicks_pmax_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtrDiff: item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches - item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtr: item?.total_ad_clicks_search && item?.total_organic_searches
            ? ((item.total_ad_clicks_search / item.total_organic_searches) * 100).toFixed(2) : 0,
        searchCtrCompare: item?.total_ad_clicks_search_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtrDiff: item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search / item.total_organic_searches - item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        organicCtr: ((item?.total_organic_clicks / item?.total_organic_searches) * 100) || 0,
        organicCtrCompare: ((item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100) || 0,
        organicCtrDiff: ((item?.total_organic_clicks / item?.total_organic_searches - item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        uncapturedPercent: ((item?.uncaptured_brand_traffic / item?.total_organic_searches) * 100) || 0,
        uncapturedPercentCompare: ((item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100) || 0,
        uncapturedPercentDiff: ((item?.uncaptured_brand_traffic / item?.total_organic_searches - item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        adCost: item?.total_ad_cost ? item.total_ad_cost.toFixed(0) : 0,
        adCostCompare: item?.total_ad_cost_compare ? item.total_ad_cost_compare.toFixed(0) : 0,
        adCostDiff: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        adCostShopping: item?.total_ad_cost_shopping ? item.total_ad_cost_shopping.toFixed(0) : 0,
        adCostShoppingCompare: item?.total_ad_cost_shopping_compare ? item.total_ad_cost_shopping_compare.toFixed(0) : 0,
        adCostShoppingDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare
            ? (item.total_ad_cost_shopping - item.total_ad_cost_shopping_compare).toFixed(0) : 0,
        adCostPmax: item?.total_ad_cost_pmax ? item.total_ad_cost_pmax.toFixed(0) : 0,
        adCostPmaxCompare: item?.total_ad_cost_pmax_compare ? item.total_ad_cost_pmax_compare.toFixed(0) : 0,
        adCostPmaxDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare
            ? (item.total_ad_cost_pmax - item.total_ad_cost_pmax_compare).toFixed(0) : 0,
        adCostSearch: item?.total_ad_cost_search ? item.total_ad_cost_search.toFixed(0) : 0,
        adCostSearchCompare: item?.total_ad_cost_search_compare ? item.total_ad_cost_search_compare.toFixed(0) : 0,
        adCostSearchDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare
            ? (item.total_ad_cost_search - item.total_ad_cost_search_compare).toFixed(0) : 0,
        blendedCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks)).toFixed(2) : 0,
        blendedCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) : 0,
        blendedCpcDiff: (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks) - item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) || 0,
        paidCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / item.total_ad_clicks).toFixed(2) : 0,
        paidCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) : 0,
        paidCpcDiff: item?.total_ad_cost && item?.total_ad_cost_compare && item?.total_ad_clicks && item?.total_ad_clicks_compare
            ? (parseFloat((item.total_ad_cost / item.total_ad_clicks).toFixed(2)) - parseFloat((item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2))) : 0,
        paidSearchCpc: item?.total_ad_cost_search && item?.total_ad_clicks_search
            ? (item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2) : 0,
        paidSearchCpcCompare: item?.total_ad_cost_search_compare && item?.total_ad_clicks_search_compare
            ? (item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2) : 0,
        paidSearchCpcDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare && item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (parseFloat((item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2))) - (parseFloat((item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2))) : 0,
        paidShoppingCpc: item?.total_ad_cost_shopping && item?.total_ad_clicks_shopping
            ? (item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2) : 0,
        paidShoppingCpcCompare: item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2) : 0,
        paidShoppingCpcDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (parseFloat((item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2))) - (parseFloat((item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2))) : 0,
        paidPmaxCpc: item?.total_ad_cost_pmax && item?.total_ad_clicks_pmax
            ? (item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2) : 0,
        paidPmaxCpcCompare: item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2) : 0,
        paidPmaxCpcDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (parseFloat((item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2))) - (parseFloat((item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2))) : 0,
        sparkline: item?.sparkline || [],
        sparkline_compare: item?.sparkline_compare || [],
        granularity: item?.granularity || "date"
    })) || [];

    const { data: countriesData, error: countriesError, isFetching: isFetchingCountries } = useGetCountriesQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        devices: filterStore?.devices,
        keywords: filterStore?.keywords,
        countries: filterStore?.countries,
        workspaceId: workspace?.id,
        platform: filterStore?.platforms,
    }, {
        skip: skipQuery || !loadedTabs.includes(1),
        refetchOnMountOrArgChange: false,
    });

    const dataCountries = countriesData?.countries?.filter((item: any) => item?.country !== null)?.map((item: any, index: any) => ({
        id: index,
        country: item?.country,
        brandSearches: item?.total_organic_searches.toFixed(0),
        brandSearchesCompare: item?.total_organic_searches_compare?.toFixed(0) || 0,
        brandSearchesDiff: (item?.total_organic_searches - item?.total_organic_searches_compare).toFixed(0) || 0,
        brandTraffic: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0) || 0,
        brandTrafficCompare: (item?.total_ad_clicks_compare + item?.total_organic_clicks_compare).toFixed(0) || 0,
        brandTrafficDiff: (
            ((item?.total_ad_clicks || 0) + (item?.total_organic_clicks || 0)) -
            ((item?.total_ad_clicks_compare || 0) + (item?.total_organic_clicks_compare || 0))
        ).toFixed(0),
        brandTrafficShopping: item?.total_ad_clicks_shopping ? item.total_ad_clicks_shopping.toFixed(0) : 0,
        brandTrafficShoppingCompare: item?.total_ad_clicks_shopping_compare ? item.total_ad_clicks_shopping_compare.toFixed(0) : 0,
        brandTrafficShoppingDiff: item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_clicks_shopping - item.total_ad_clicks_shopping_compare).toFixed(0) : 0,
        brandTrafficPmax: item?.total_ad_clicks_pmax ? item.total_ad_clicks_pmax.toFixed(0) : 0,
        brandTrafficPmaxCompare: item?.total_ad_clicks_pmax_compare ? item.total_ad_clicks_pmax_compare.toFixed(0) : 0,
        brandTrafficPmaxDiff: item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_clicks_pmax - item.total_ad_clicks_pmax_compare).toFixed(0) : 0,
        brandTrafficSearch: item?.total_ad_clicks_search ? item.total_ad_clicks_search.toFixed(0) : 0,
        brandTrafficSearchCompare: item?.total_ad_clicks_search_compare ? item.total_ad_clicks_search_compare.toFixed(0) : 0,
        brandTrafficSearchDiff: item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (item.total_ad_clicks_search - item.total_ad_clicks_search_compare).toFixed(0) : 0,
        organicBrandTraffic: item?.total_organic_clicks?.toFixed(0) || 0,
        organicBrandTrafficCompare: item?.total_organic_clicks_compare?.toFixed(0) || 0,
        organicBrandTrafficDiff: (item?.total_organic_clicks - item?.total_organic_clicks_compare)?.toFixed(0) || 0,
        paidBrandTraffic: item?.total_ad_clicks?.toFixed(0) || 0,
        paidBrandTrafficCompare: item?.total_ad_clicks_compare?.toFixed(0) || 0,
        paidBrandTrafficDiff: (item?.total_ad_clicks - item?.total_ad_clicks_compare)?.toFixed(0) || 0,
        uncapturedBrandTraffic: item?.uncaptured_brand_traffic?.toFixed(0) || 0,
        uncapturedBrandTrafficCompare: item?.uncaptured_brand_traffic_compare?.toFixed(0) || 0,
        uncapturedBrandTrafficDiff: (item?.uncaptured_brand_traffic - item?.uncaptured_brand_traffic_compare)?.toFixed(0) || 0,
        blendedCtr: item?.blended_ctr || 0,
        blendedCtrCompare: item?.blended_ctr_compare || 0,
        blendedCtrDiff: (Math.min(item?.blended_ctr, 100) - Math.min(item?.blended_ctr_compare, 100)).toFixed(2) || 0,
        paidCtr: item?.total_ad_clicks && item?.total_organic_searches
            ? ((item.total_ad_clicks / item.total_organic_searches) * 100).toFixed(2) : 0,
        paidCtrCompare: item?.total_ad_clicks_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        paidCtrDiff: (Math.min((item.total_ad_clicks / item.total_organic_searches) * 100, 100) - Math.min((item.total_ad_clicks_compare / item.total_organic_searches_compare) * 100, 100))?.toFixed(2),
        shoppingCtr: item?.total_ad_clicks_shopping && item?.total_organic_searches
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches) * 100).toFixed(2) : 0,
        shoppingCtrCompare: item?.total_ad_clicks_shopping_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        shoppingCtrDiff: item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches - item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtr: item?.total_ad_clicks_pmax && item?.total_organic_searches
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches) * 100).toFixed(2) : 0,
        pmaxCtrCompare: item?.total_ad_clicks_pmax_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtrDiff: item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches - item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtr: item?.total_ad_clicks_search && item?.total_organic_searches
            ? ((item.total_ad_clicks_search / item.total_organic_searches) * 100).toFixed(2) : 0,
        searchCtrCompare: item?.total_ad_clicks_search_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtrDiff: item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search / item.total_organic_searches - item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        organicCtr: ((item?.total_organic_clicks / item?.total_organic_searches) * 100) || 0,
        organicCtrCompare: ((item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100) || 0,
        organicCtrDiff: ((item?.total_organic_clicks / item?.total_organic_searches - item?.total_organic_clicks_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        uncapturedPercent: ((item?.uncaptured_brand_traffic / item?.total_organic_searches) * 100) || 0,
        uncapturedPercentCompare: ((item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100) || 0,
        uncapturedPercentDiff: ((item?.uncaptured_brand_traffic / item?.total_organic_searches - item?.uncaptured_brand_traffic_compare / item?.total_organic_searches_compare) * 100).toFixed(2) || 0,
        adCost: item?.total_ad_cost ? item.total_ad_cost.toFixed(0) : 0,
        adCostCompare: item?.total_ad_cost_compare ? item.total_ad_cost_compare.toFixed(0) : 0,
        adCostDiff: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        adCostShopping: item?.total_ad_cost_shopping ? item.total_ad_cost_shopping.toFixed(0) : 0,
        adCostShoppingCompare: item?.total_ad_cost_shopping_compare ? item.total_ad_cost_shopping_compare.toFixed(0) : 0,
        adCostShoppingDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare
            ? (item.total_ad_cost_shopping - item.total_ad_cost_shopping_compare).toFixed(0) : 0,
        adCostPmax: item?.total_ad_cost_pmax ? item.total_ad_cost_pmax.toFixed(0) : 0,
        adCostPmaxCompare: item?.total_ad_cost_pmax_compare ? item.total_ad_cost_pmax_compare.toFixed(0) : 0,
        adCostPmaxDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare
            ? (item.total_ad_cost_pmax - item.total_ad_cost_pmax_compare).toFixed(0) : 0,
        adCostSearch: item?.total_ad_cost_search ? item.total_ad_cost_search.toFixed(0) : 0,
        adCostSearchCompare: item?.total_ad_cost_search_compare ? item.total_ad_cost_search_compare.toFixed(0) : 0,
        adCostSearchDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare
            ? (item.total_ad_cost_search - item.total_ad_cost_search_compare).toFixed(0) : 0,
        blendedCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks)).toFixed(2) : 0,
        blendedCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) : 0,
        blendedCpcDiff: (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks) - item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) || 0,
        paidCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / item.total_ad_clicks).toFixed(2) : 0,
        paidCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) : 0,
        paidCpcDiff: item?.total_ad_cost && item?.total_ad_cost_compare && item?.total_ad_clicks && item?.total_ad_clicks_compare
            ? (parseFloat((item.total_ad_cost / item.total_ad_clicks).toFixed(2)) - parseFloat((item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2))) : 0,
        paidSearchCpc: item?.total_ad_cost_search && item?.total_ad_clicks_search
            ? (item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2) : 0,
        paidSearchCpcCompare: item?.total_ad_cost_search_compare && item?.total_ad_clicks_search_compare
            ? (item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2) : 0,
        paidSearchCpcDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare && item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (parseFloat((item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2))) - (parseFloat((item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2))) : 0,
        paidShoppingCpc: item?.total_ad_cost_shopping && item?.total_ad_clicks_shopping
            ? (item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2) : 0,
        paidShoppingCpcCompare: item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2) : 0,
        paidShoppingCpcDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (parseFloat((item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2))) - (parseFloat((item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2))) : 0,
        paidPmaxCpc: item?.total_ad_cost_pmax && item?.total_ad_clicks_pmax
            ? (item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2) : 0,
        paidPmaxCpcCompare: item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2) : 0,
        paidPmaxCpcDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (parseFloat((item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2))) - (parseFloat((item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2))) : 0,
        sparkline: item?.sparkline,
        sparkline_compare: item?.sparkline_compare,
        granularity: item?.granularity || "date"
    })) || [];

    const { data: statsData, error: statsError, isFetching: isFetchingStats } = useGetStatsDashCustomQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        device: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        workspaceId: workspace?.id,
        platform: filterStore?.platforms
    }, {
        skip: skipQuery,
        refetchOnMountOrArgChange: false,

    });

    const { data: statsDataDay, error: statsDayError, isFetching: isFetchingStatsDay } = useGetStatsQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        device: filterStore.devices,
        countries: filterStore.countries,
        workspaceId: workspace?.id,
        keywords: filterStore.keywords,
        timestep: filterStore?.timestep,
        platform: filterStore?.platforms,
    }, {
        skip: skipQuery || !loadedTabs.includes(3),
        refetchOnMountOrArgChange: false,
    });

    useEffect(() => {
        dispatch(setFilterMinDatePicker(statsData?.minDatePicker[0]?.first_date))
    }, [statsData])

    const dataDay = statsDataDay?.stats?.map((item: any, index: any) => ({
        id: index,
        date: item[filterStore?.timestep],
        brandSearches: item?.total_organic_searches.toFixed(0),
        organicBrandTraffic: item?.total_organic_clicks?.toFixed(0),
        paidBrandTraffic: item?.total_ad_clicks?.toFixed(0),
        brandTrafficShopping: item?.total_ad_clicks_shopping?.toFixed(0),
        brandTrafficPmax: item?.total_ad_clicks_pmax?.toFixed(0),
        brandTrafficSearch: item?.total_ad_clicks_search?.toFixed(0),
        uncapturedBrandTraffic: item?.uncaptured_brand_traffic?.toFixed(0),
        blendedCtr: (item?.blended_ctr * 100),
        paidCtr: (item?.total_ad_clicks / item?.total_organic_searches * 100),
        shoppingCtr: item?.total_ad_clicks_shopping && item?.total_organic_searches
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches) * 100).toFixed(2) : 0,
        shoppingCtrCompare: item?.total_ad_clicks_shopping_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        shoppingCtrDiff: item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_shopping / item.total_organic_searches - item.total_ad_clicks_shopping_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtr: item?.total_ad_clicks_pmax && item?.total_organic_searches
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches) * 100).toFixed(2) : 0,
        pmaxCtrCompare: item?.total_ad_clicks_pmax_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        pmaxCtrDiff: item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_pmax / item.total_organic_searches - item.total_ad_clicks_pmax_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtr: item?.total_ad_clicks_search && item?.total_organic_searches
            ? ((item.total_ad_clicks_search / item.total_organic_searches) * 100).toFixed(2) : 0,
        searchCtrCompare: item?.total_ad_clicks_search_compare && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        searchCtrDiff: item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare && item?.total_organic_searches && item?.total_organic_searches_compare
            ? ((item.total_ad_clicks_search / item.total_organic_searches - item.total_ad_clicks_search_compare / item.total_organic_searches_compare) * 100).toFixed(2) : 0,
        organicCtr: (item?.total_organic_clicks / item?.total_organic_searches * 100),
        uncapturedPercent: (item?.uncaptured_brand_traffic / item?.total_organic_searches * 100),
        adCost: item?.total_ad_cost ? item.total_ad_cost.toFixed(0) : 0,
        adCostCompare: item?.total_ad_cost_compare ? item.total_ad_cost_compare.toFixed(0) : 0,
        adCostDiff: item?.total_ad_cost && item?.total_ad_cost_compare
            ? (item.total_ad_cost - item.total_ad_cost_compare).toFixed(0) : 0,
        adCostShopping: item?.total_ad_cost_shopping ? item.total_ad_cost_shopping.toFixed(0) : 0,
        adCostShoppingCompare: item?.total_ad_cost_shopping_compare ? item.total_ad_cost_shopping_compare.toFixed(0) : 0,
        adCostShoppingDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare
            ? (item.total_ad_cost_shopping - item.total_ad_cost_shopping_compare).toFixed(0) : 0,
        adCostPmax: item?.total_ad_cost_pmax ? item.total_ad_cost_pmax.toFixed(0) : 0,
        adCostPmaxCompare: item?.total_ad_cost_pmax_compare ? item.total_ad_cost_pmax_compare.toFixed(0) : 0,
        adCostPmaxDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare
            ? (item.total_ad_cost_pmax - item.total_ad_cost_pmax_compare).toFixed(0) : 0,
        adCostSearch: item?.total_ad_cost_search ? item.total_ad_cost_search.toFixed(0) : 0,
        adCostSearchCompare: item?.total_ad_cost_search_compare ? item.total_ad_cost_search_compare.toFixed(0) : 0,
        adCostSearchDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare
            ? (item.total_ad_cost_search - item.total_ad_cost_search_compare).toFixed(0) : 0,
        totalBrandTraffic: (item?.total_ad_clicks + item?.total_organic_clicks).toFixed(0),
        blendedCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks)).toFixed(2) : 0,
        blendedCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) : 0,
        blendedCpcDiff: (item.total_ad_cost / (item.total_ad_clicks + item.total_organic_clicks) - item.total_ad_cost_compare / (item.total_ad_clicks_compare + item.total_organic_clicks_compare)).toFixed(2) || 0,
        paidCpc: item?.total_ad_cost && item?.total_ad_clicks
            ? (item.total_ad_cost / item.total_ad_clicks).toFixed(2) : 0,
        paidCpcCompare: item?.total_ad_cost_compare && item?.total_ad_clicks_compare
            ? (item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2) : 0,
        paidCpcDiff: item?.total_ad_cost && item?.total_ad_cost_compare && item?.total_ad_clicks && item?.total_ad_clicks_compare
            ? (parseFloat((item.total_ad_cost / item.total_ad_clicks).toFixed(2)) - parseFloat((item.total_ad_cost_compare / item.total_ad_clicks_compare).toFixed(2))) : 0,
        paidSearchCpc: item?.total_ad_cost_search && item?.total_ad_clicks_search
            ? (item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2) : 0,
        paidSearchCpcCompare: item?.total_ad_cost_search_compare && item?.total_ad_clicks_search_compare
            ? (item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2) : 0,
        paidSearchCpcDiff: item?.total_ad_cost_search && item?.total_ad_cost_search_compare && item?.total_ad_clicks_search && item?.total_ad_clicks_search_compare
            ? (parseFloat((item.total_ad_cost_search / item.total_ad_clicks_search).toFixed(2))) - (parseFloat((item.total_ad_cost_search_compare / item.total_ad_clicks_search_compare).toFixed(2))) : 0,
        paidShoppingCpc: item?.total_ad_cost_shopping && item?.total_ad_clicks_shopping
            ? (item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2) : 0,
        paidShoppingCpcCompare: item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping_compare
            ? (item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2) : 0,
        paidShoppingCpcDiff: item?.total_ad_cost_shopping && item?.total_ad_cost_shopping_compare && item?.total_ad_clicks_shopping && item?.total_ad_clicks_shopping_compare
            ? (parseFloat((item.total_ad_cost_shopping / item.total_ad_clicks_shopping).toFixed(2))) - (parseFloat((item.total_ad_cost_shopping_compare / item.total_ad_clicks_shopping_compare).toFixed(2))) : 0,
        paidPmaxCpc: item?.total_ad_cost_pmax && item?.total_ad_clicks_pmax
            ? (item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2) : 0,
        paidPmaxCpcCompare: item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax_compare
            ? (item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2) : 0,
        paidPmaxCpcDiff: item?.total_ad_cost_pmax && item?.total_ad_cost_pmax_compare && item?.total_ad_clicks_pmax && item?.total_ad_clicks_pmax_compare
            ? (parseFloat((item.total_ad_cost_pmax / item.total_ad_clicks_pmax).toFixed(2))) - (parseFloat((item.total_ad_cost_pmax_compare / item.total_ad_clicks_pmax_compare).toFixed(2))) : 0,

    })) || [];

    const createSparkLineData = (
        data: any[],
        getValue: (item: any) => number,
        filterStore: any,
    ) => {
        return data?.map((item: any) => {
            const daysDifference = getDaysDifference(
                filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
                filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null
            );

            const value = getValue(item);

            if (daysDifference > 365) {
                return { y: value, x: item?.year_month };
            } else if (daysDifference > 90) {
                return { y: value, x: item?.year_week };
            } else {
                const date = new Date(item?.date);
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                return { y: value, x: `${year}-${month}-${day}` };
            }
        });
    };

    const sparkLineConfigs = [
        {
            name: 'BrandSearches',
            getValue: (item: any) => (item?.total_organic_clicks || 0) + (item?.total_ad_clicks || 0) + (item?.uncaptured_brand_traffic || 0)
        },
        {
            name: 'TotalBrandTraffic',
            getValue: (item: any) => (item?.total_organic_clicks || 0) + (item?.total_ad_clicks || 0)
        },
        {
            name: 'OrganicBrandTraffic',
            getValue: (item: any) => item?.total_organic_clicks || 0
        },
        {
            name: 'AdBrandTraffic',
            getValue: (item: any) => item?.total_ad_clicks || 0
        },
        {
            name: 'BlendedCtr',
            getValue: (item: any) => {
                const totalClicks = (item?.total_ad_clicks || 0) + (item?.total_organic_clicks || 0);
                const totalSearches = item?.total_organic_searches || 1;
                return (totalClicks / totalSearches) * 100;
            }
        },
        {
            name: 'TotalAdCost',
            getValue: (item: any) => item?.total_ad_cost || 0
        },
        {
            name: 'Cpc',
            getValue: (item: any) => {
                const totalAdClicks = item?.total_ad_clicks || 0;
                return totalAdClicks > 0 ? (item?.total_ad_cost || 0) / totalAdClicks : 0;
            }
        },
        {
            name: 'SearchBrandTraffic',
            getValue: (item: any) => item?.search_brand_traffic || 0
        },
        {
            name: 'ShoppingBrandTraffic',
            getValue: (item: any) => item?.shopping_brand_traffic || 0
        },
        {
            name: 'PmaxBrandTraffic',
            getValue: (item: any) => item?.pmax_brand_traffic || 0
        },
        {
            name: 'OrganicCtr',
            getValue: (item: any) => item?.organic_ctr || 0
        },
        {
            name: 'PaidCtr',
            getValue: (item: any) => item?.paid_ctr || 0
        },
        {
            name: 'ShoppingPaidCtr',
            getValue: (item: any) => item?.shopping_paid_ctr || 0
        },
        {
            name: 'SearchPaidCtr',
            getValue: (item: any) => item?.search_paid_ctr || 0
        },
        {
            name: 'SearchAdCost',
            getValue: (item: any) => item?.search_ad_cost || 0
        },
        {
            name: 'ShoppingAdCost',
            getValue: (item: any) => item?.shopping_ad_cost || 0
        },
        {
            name: 'PmaxAdCost',
            getValue: (item: any) => item?.total_ad_cost_pmax || 0
        },
        {
            name: 'SearchCpc',
            getValue: (item: any) => item?.search_cpc || 0
        },
        {
            name: 'ShoppingCpc',
            getValue: (item: any) => item?.shopping_cpc || 0
        },
        {
            name: 'PmaxCpc',
            getValue: (item: any) => item?.pmax_cpc || 0
        },
        {
            name: 'PmaxPaidCtr',
            getValue: (item: any) => item?.pmax_paid_ctr || 0
        },
        {
            name: 'BlendedCpc',
            getValue: (item: any) => {
                const totalClicks = (item?.total_organic_clicks || 0) + (item?.total_ad_clicks || 0);
                return totalClicks > 0 ? (item?.total_ad_cost || 0) / totalClicks : 0;
            }
        },
    ];

    const SparkLinesData = sparkLineConfigs.reduce((acc, config) => {
        acc[`SparkLinesData${config.name}`] = createSparkLineData(
            data?.stats,
            config.getValue,
            filterStore,
        );
        return acc;
    }, {} as Record<string, any[]>);

    // All available stats including hidden ones
    const allStats = [
        {
            title: t('dashboard.brandTraffic.brandSearches'),
            key: "brandSearches",
            value: statsData?.totals.organic_searches,
            valueCompare: statsData?.compare_totals.organic_searches,
            compareData: ((statsData?.totals.organic_searches - statsData?.compare_totals.organic_searches) / statsData?.compare_totals.organic_searches) * 100,
            color: kpiToogle?.includes("brandSearches") ? ColorSchema.BRAND_SEARCH : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#b71d18' : kpiToogle?.includes("brandSearches") ? '#b71d18' : '#e2e5e9',
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("brandSearches") ? '#db8377' : undefined : 'transparent',
            trendColor: "#b71d18",
            icon: "",
            series: SparkLinesData.SparkLinesDataBrandSearches,
            active: kpiToogle?.includes("brandSearches")
        },
        {
            title: t('dashboard.brandTraffic.totalBrandTraffic'),
            key: "totalBrandTraffic",
            value: statsData?.totals.brand_traffic,
            valueCompare: statsData?.compare_totals.brand_traffic,
            compareData: ((statsData?.totals.brand_traffic - statsData?.compare_totals.brand_traffic) / statsData?.compare_totals.brand_traffic) * 100,
            color: kpiToogle?.includes("totalBrandTraffic") ? ColorSchema.SECONDARY : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#880e4f' : kpiToogle?.includes("totalBrandTraffic") ? '#880e4f' : '#e2e5e9',
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("totalBrandTraffic") ? '#a64b7b' : undefined : 'transparent',
            trendColor: kpiToogle?.includes("totalBrandTraffic") ? "#880e4f" : '#e2e5e9',
            icon: "",
            series: SparkLinesData.SparkLinesDataTotalBrandTraffic,
            active: kpiToogle?.includes("totalBrandTraffic")
        },
        {
            title: t('dashboard.brandTraffic.organic_brand_traffic'),
            key: "organicBrandTraffic",
            value: statsData?.totals.organic_clicks,
            valueCompare: statsData?.compare_totals.organic_clicks,
            compareData: ((statsData?.totals.organic_clicks - statsData?.compare_totals.organic_clicks) / statsData?.compare_totals.organic_clicks) * 100,
            color: kpiToogle?.includes("organicBrandTraffic") ? ColorSchema.SUCCESS : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#118d57' : kpiToogle?.includes("organicBrandTraffic") ? "#118d57" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("organicBrandTraffic") ? '#6dad8d' : undefined : 'transparent',
            trendColor: kpiToogle?.includes("organicBrandTraffic") ? "#118d57" : '#e2e5e9',
            icon: "",
            series: SparkLinesData.SparkLinesDataOrganicBrandTraffic,
        },
        {
            title: t('dashboard.brandTraffic.ad_brand_traffic'),
            key: "adBrandTraffic",
            value: statsData?.totals.ad_clicks,
            valueCompare: statsData?.compare_totals.ad_clicks,
            compareData: ((statsData?.totals.ad_clicks - statsData?.compare_totals.ad_clicks) / statsData?.compare_totals.ad_clicks) * 100,
            color: kpiToogle?.includes("adBrandTraffic") ? ColorSchema.TERTIARY : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#006c9c' : kpiToogle?.includes("adBrandTraffic") ? "#006c9c" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("adBrandTraffic") ? '#65b5c9' : undefined : 'transparent',
            trendColor: kpiToogle?.includes("adBrandTraffic") ? "#006c9c" : '#e2e5e9',
            icon: "",
            series: SparkLinesData.SparkLinesDataAdBrandTraffic,
        },
        {
            title: t('dashboard.brandTraffic.blendedCtr'),
            key: "blendedCtr",
            valueCompare: statsData?.compare_totals.blended_ctr,
            value: statsData?.totals.blended_ctr,
            compareData: ((statsData?.totals.blended_ctr - statsData?.compare_totals.blended_ctr) / statsData?.compare_totals.blended_ctr) * 100,
            color: kpiToogle?.includes("blendedCtr") ? ColorSchema.INFO : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#5119b7' : kpiToogle?.includes("blendedCtr") ? "#5119b7" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("blendedCtr") ? '#9e77da' : undefined : 'transparent',
            trendColor: kpiToogle?.includes("blendedCtr") ? "#5119b7" : '#e2e5e9',
            icon: "",
            series: SparkLinesData.SparkLinesDataBlendedCtr
        },
        {
            title: t('dashboard.global.ad_cost'),
            key: "adCost",
            value: statsData?.totals.ad_cost,
            valueCompare: statsData?.compare_totals.ad_cost,
            compareData: ((statsData?.totals.ad_cost - statsData?.compare_totals.ad_cost) / statsData?.compare_totals.ad_cost) * 100,
            color: kpiToogle?.includes("adCost") ? ColorSchema.WARNING : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#FFAB00' : kpiToogle?.includes("adCost") ? "#b76e00" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("adCost") ? '#FFE5B2' : undefined : 'transparent',
            trendColor: kpiToogle?.includes("adCost") ? "#b76e00" : '#e2e5e9',
            icon: "",
            series: SparkLinesData.SparkLinesDataTotalAdCost
        },
        {
            title: t('dashboard.global.blended_cpc'),
            key: "blendedCpc",
            value: statsData?.totals.ad_cost / (statsData?.totals.organic_clicks + statsData?.totals.ad_clicks),
            valueCompare: statsData?.compare_totals.ad_cost / (statsData?.compare_totals.organic_clicks + statsData?.compare_totals.ad_clicks),
            compareData: ((statsData?.totals.ad_cost / (statsData?.totals.organic_clicks + statsData?.totals.ad_clicks)) -
                (statsData?.compare_totals.ad_cost / (statsData?.compare_totals.organic_clicks + statsData?.compare_totals.ad_clicks))) /
                (statsData?.compare_totals.ad_cost / (statsData?.compare_totals.organic_clicks + statsData?.compare_totals.ad_clicks)) * 100,
            color: kpiToogle?.includes("blendedCpc") ? ColorSchema.BLENDED_CPC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? theme.palette.metrics.blendedCpc.main : kpiToogle?.includes("blendedCpc") ? theme.palette.metrics.blendedCpc.main : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("blendedCpc") ? theme.palette.metrics.blendedCpc.light : undefined : 'transparent',
            trendColor: "#FF5630",
            icon: "",
            series: SparkLinesData.SparkLinesDataBlendedCpc
        },
        {
            title: t('dashboard.global.search_brand_traffic'),
            key: "searchBrandTraffic",
            value: statsData?.totals.search_brand_traffic,
            valueCompare: statsData?.compare_totals.search_brand_traffic,
            compareData: statsData?.compare_totals.search_brand_traffic ? ((statsData?.totals.search_brand_traffic - statsData?.compare_totals.search_brand_traffic) / statsData?.compare_totals.search_brand_traffic) * 100 : 0,
            color: kpiToogle?.includes("searchBrandTraffic") ? ColorSchema.SEARCH_BRAND_TRAFFIC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SEARCH_BRAND_TRAFFIC : kpiToogle?.includes("searchBrandTraffic") ? theme.palette.metrics.searchBrandTraffic.dark : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("searchBrandTraffic") ? "#BBDBF7" : undefined : 'transparent',
            trendColor: "#155FA0",
            icon: "",
            series: SparkLinesData.SparkLinesDataSearchBrandTraffic
        },
        {
            title: t('dashboard.global.shopping_brand_traffic'),
            key: "shoppingBrandTraffic",
            value: statsData?.totals.shopping_brand_traffic,
            valueCompare: statsData?.compare_totals.shopping_brand_traffic,
            compareData: statsData?.compare_totals.shopping_brand_traffic ? ((statsData?.totals.shopping_brand_traffic - statsData?.compare_totals.shopping_brand_traffic) / statsData?.compare_totals.shopping_brand_traffic) * 100 : 0,
            color: kpiToogle?.includes("shoppingBrandTraffic") ? ColorSchema.SHOPPING_BRAND_TRAFFIC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SHOPPING_BRAND_TRAFFIC : kpiToogle?.includes("shoppingBrandTraffic") ? "#063860" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("shoppingBrandTraffic") ? "#D0E8FC" : undefined : 'transparent',
            trendColor: "#467EAC",
            icon: "",
            series: SparkLinesData.SparkLinesDataShoppingBrandTraffic
        },
        {
            title: t('dashboard.global.pmax_brand_traffic'),
            key: "pmaxBrandTraffic",
            value: statsData?.totals.pmax_brand_traffic,
            valueCompare: statsData?.compare_totals.pmax_brand_traffic,
            compareData: statsData?.compare_totals.pmax_brand_traffic ? ((statsData?.totals.pmax_brand_traffic - statsData?.compare_totals.pmax_brand_traffic) / statsData?.compare_totals.pmax_brand_traffic) * 100 : 0,
            color: kpiToogle?.includes("pmaxBrandTraffic") ? ColorSchema.PMAX_BRAND_TRAFFIC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.PMAX_BRAND_TRAFFIC : kpiToogle?.includes("pmaxBrandTraffic") ? "#0B6FC1" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("pmaxBrandTraffic") ? "#FFFFFF" : undefined : 'transparent',
            trendColor: "#B7DCFB",
            icon: "",
            series: SparkLinesData.SparkLinesDataPmaxBrandTraffic
        },
        {
            title: t('dashboard.global.organic_ctr'),
            key: "organicCtr",
            value: statsData?.totals.organic_ctr,
            valueCompare: statsData?.compare_totals.organic_ctr,
            compareData: statsData?.compare_totals.organic_ctr ? ((statsData?.totals.organic_ctr - statsData?.compare_totals.organic_ctr) / statsData?.compare_totals.organic_ctr) * 100 : 0,
            color: kpiToogle?.includes("organicCtr") ? ColorSchema.ORGANIC_CTR : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.ORGANIC_CTR : kpiToogle?.includes("organicCtr") ? "#03261E" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("organicCtr") ? "#B7DCCC" : undefined : 'transparent',
            trendColor: "#085F4A",
            icon: "",
            series: SparkLinesData.SparkLinesDataOrganicCtr
        },
        {
            title: t('dashboard.global.paid_ctr'),
            key: "paidCtr",
            value: statsData?.totals.paid_ctr,
            valueCompare: statsData?.compare_totals.paid_ctr,
            compareData: statsData?.compare_totals.paid_ctr ? ((statsData?.totals.paid_ctr - statsData?.compare_totals.paid_ctr) / statsData?.compare_totals.paid_ctr) * 100 : 0,
            color: kpiToogle?.includes("paidCtr") ? ColorSchema.PAID_CTR : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.PAID_CTR : kpiToogle?.includes("paidCtr") ? "#011C28" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("paidCtr") ? '#B2D2E1' : undefined : 'transparent',
            trendColor: "#026D9C",
            icon: "",
            series: SparkLinesData.SparkLinesDataPaidCtr
        },
        {
            title: t('dashboard.global.search_paid_ctr'),
            key: "searchPaidCtr",
            value: statsData?.totals.search_paid_ctr,
            valueCompare: statsData?.compare_totals.search_paid_ctr,
            compareData: statsData?.compare_totals.search_paid_ctr ? ((statsData?.totals.search_paid_ctr - statsData?.compare_totals.search_paid_ctr) / statsData?.compare_totals.search_paid_ctr) * 100 : 0,
            color: kpiToogle?.includes("searchPaidCtr") ? ColorSchema.SEARCH_PAID_CTR : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? "#1E88E5" : kpiToogle?.includes("searchPaidCtr") ? "#370620" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("searchPaidCtr") ? '#BBDBF7' : undefined : 'transparent',
            trendColor: "#1E88E5",
            icon: "",
            series: SparkLinesData.SparkLinesDataSearchPaidCtr
        },
        {
            title: t('dashboard.global.shopping_paid_ctr'),
            key: "shoppingPaidCtr",
            value: statsData?.totals.shopping_paid_ctr,
            valueCompare: statsData?.compare_totals.shopping_paid_ctr,
            compareData: statsData?.compare_totals.shopping_paid_ctr ? ((statsData?.totals.shopping_paid_ctr - statsData?.compare_totals.shopping_paid_ctr) / statsData?.compare_totals.shopping_paid_ctr) * 100 : 0,
            color: kpiToogle?.includes("shoppingPaidCtr") ? ColorSchema.SHOPPING_PAID_CTR : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SHOPPING_PAID_CTR : kpiToogle?.includes("shoppingPaidCtr") ? "#063860" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("shoppingPaidCtr") ? '#D0E8FC' : undefined : 'transparent',
            trendColor: "#64B5F6",
            icon: "",
            series: SparkLinesData.SparkLinesDataShoppingPaidCtr
        },
        {
            title: t('dashboard.global.pmax_paid_ctr'),
            key: "pmaxPaidCtr",
            value: statsData?.totals.pmax_paid_ctr,
            valueCompare: statsData?.compare_totals.pmax_paid_ctr,
            compareData: statsData?.compare_totals.pmax_paid_ctr ? ((statsData?.totals.pmax_paid_ctr - statsData?.compare_totals.pmax_paid_ctr) / statsData?.compare_totals.pmax_paid_ctr) * 100 : 0,
            color: kpiToogle?.includes("pmaxPaidCtr") ? ColorSchema.PMAX_PAID_CTR : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.PMAX_PAID_CTR : kpiToogle?.includes("pmaxPaidCtr") ? "#0C7AD4" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("pmaxPaidCtr") ? '#FFFFFF' : undefined : 'transparent',
            trendColor: "#B7DCFB",
            icon: "",
            series: SparkLinesData.SparkLinesDataPmaxPaidCtr
        },
        {
            title: t('dashboard.global.search_ad_cost'),
            key: "searchAdCost",
            value: statsData?.totals.search_ad_cost,
            valueCompare: statsData?.compare_totals.search_ad_cost,
            compareData: statsData?.compare_totals.search_ad_cost ? ((statsData?.totals.search_ad_cost - statsData?.compare_totals.search_ad_cost) / statsData?.compare_totals.search_ad_cost) * 100 : 0,
            color: kpiToogle?.includes("searchAdCost") ? ColorSchema.SEARCH_AD_COST : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SEARCH_AD_COST : kpiToogle?.includes("searchAdCost") ? "#664700" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("searchAdCost") ? '#FFE8B2' : undefined : 'transparent',
            trendColor: "#FFB300",
            icon: "",
            series: SparkLinesData.SparkLinesDataSearchAdCost
        },
        {
            title: t('dashboard.global.shopping_ad_cost'),
            key: "shoppingAdCost",
            value: statsData?.totals.shopping_ad_cost,
            valueCompare: statsData?.compare_totals.shopping_ad_cost,
            compareData: statsData?.compare_totals.shopping_ad_cost ? ((statsData?.totals.shopping_ad_cost - statsData?.compare_totals.shopping_ad_cost) / statsData?.compare_totals.shopping_ad_cost) * 100 : 0,
            color: kpiToogle?.includes("shoppingAdCost") ? ColorSchema.SHOPPING_AD_COST : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SHOPPING_AD_COST : kpiToogle?.includes("shoppingAdCost") ? "#A37D00" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("shoppingAdCost") ? "#fff" : undefined : 'transparent',
            trendColor: "#FFD54F",
            icon: "",
            series: SparkLinesData.SparkLinesDataShoppingAdCost
        },
        {
            title: t('dashboard.global.pmax_ad_cost'),
            key: "pmaxAdCost",
            value: statsData?.totals.pmax_ad_cost,
            valueCompare: statsData?.compare_totals.pmax_ad_cost,
            compareData: statsData?.compare_totals.pmax_ad_cost ? ((statsData?.totals.pmax_ad_cost - statsData?.compare_totals.pmax_ad_cost) / statsData?.compare_totals.pmax_ad_cost) * 100 : 0,
            color: kpiToogle?.includes("pmaxAdCost") ? ColorSchema.PMAX_AD_COST : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.PMAX_AD_COST : kpiToogle?.includes("pmaxAdCost") ? "#E0AC00" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("pmaxAdCost") ? "#FFFFFF" : undefined : 'transparent',
            trendColor: "#FFE180",
            icon: "",
            series: SparkLinesData.SparkLinesDataPmaxAdCost
        },
        {
            title: t('dashboard.global.cpc'),
            key: "cpc",
            value: statsData?.totals.cpc,
            valueCompare: statsData?.compare_totals.cpc,
            compareData: ((statsData?.totals.cpc - statsData?.compare_totals.cpc) / statsData?.compare_totals.cpc) * 100,
            color: kpiToogle?.includes("cpc") ? ColorSchema.CPC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? "#FF5630" : kpiToogle?.includes("cpc") ? theme.palette.cpc.dark : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("cpc") ? "#FFAC99" : undefined : 'transparent',
            trendColor: ColorSchema.CPC,
            icon: "",
            series: SparkLinesData.SparkLinesDataCpc
        },
        {
            title: t('dashboard.global.search_cpc'),
            key: "searchCpc",
            value: statsData?.totals.search_cpc,
            valueCompare: statsData?.compare_totals.search_cpc,
            compareData: statsData?.compare_totals.search_cpc ? ((statsData?.totals.search_cpc - statsData?.compare_totals.search_cpc) / statsData?.compare_totals.search_cpc) * 100 : 0,
            color: kpiToogle?.includes("searchCpc") ? ColorSchema.SEARCH_CPC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? '#FF5630' : kpiToogle?.includes("searchCpc") ? "#FF5630" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("searchCpc") ? '#FFCCC0' : undefined : 'transparent',
            trendColor: '#FF5630',
            icon: "",
            series: SparkLinesData.SparkLinesDataSearchCpc
        },
        {
            title: t('dashboard.global.shopping_cpc'),
            key: "shoppingCpc",
            value: statsData?.totals.shopping_cpc,
            valueCompare: statsData?.compare_totals.shopping_cpc,
            compareData: statsData?.compare_totals.shopping_cpc ? ((statsData?.totals.shopping_cpc - statsData?.compare_totals.shopping_cpc) / statsData?.compare_totals.shopping_cpc) * 100 : 0,
            color: kpiToogle?.includes("shoppingCpc") ? ColorSchema.SHOPPING_CPC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.SHOPPING_CPC : kpiToogle?.includes("shoppingCpc") ? "#E04B00" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("shoppingCpc") ? '#FFE6D9' : undefined : 'transparent',
            trendColor: "#FFAC82",
            icon: "",
            series: SparkLinesData.SparkLinesDataShoppingCpc
        },
        {
            title: t('dashboard.global.pmax_cpc'),
            key: "pmaxCpc",
            value: statsData?.totals.pmax_cpc,
            valueCompare: statsData?.compare_totals.pmax_cpc,
            compareData: statsData?.compare_totals.pmax_cpc ? ((statsData?.totals.pmax_cpc - statsData?.compare_totals.pmax_cpc) / statsData?.compare_totals.pmax_cpc) * 100 : 0,
            color: kpiToogle?.includes("pmaxCpc") ? ColorSchema.PMAX_CPC : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT,
            textColor: theme.palette.mode === "dark" ? ColorSchema.PMAX_CPC : kpiToogle?.includes("pmaxCpc") ? "#FF8B1F" : "#e2e5e9",
            spanColor: theme.palette.mode === "dark" ? kpiToogle?.includes("pmaxCpc") ? '#FFFFFF' : undefined : 'transparent',
            trendColor: "#FFE9D5",
            icon: "",
            series: SparkLinesData.SparkLinesDataPmaxCpc
        },
    ].flat(); // Utiliser flat() pour aplatir le tableau après les spreads conditionnels
    // Get visible stats based on kpiToogle
    const visibleStatsData = allStats.filter(stat => visibleStats.includes(stat.key));

    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
    };

    const handleFilterModelChange = (newModel: any) => {
        setFilterModel(newModel);
        localStorage.setItem("dataGridFilters", JSON.stringify(newModel));
    };

    const handleToggleKPI = (key: string) => {
        setKPIToogle((prevState: string[] = []) => {
            // Log the number of active pinned metrics
            const pinnedActive = prevState.filter((k) => pinnedMetrics.includes(k));
            // Prevent removing the last active metric
            if (prevState.includes(key)) {
                if (prevState.length <= 1) {
                    enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_item'), { variant: 'error' });
                    return prevState;
                }

                return prevState.filter((k: string) => k !== key);
            } else {
                // Activate the metric
                // @ts-ignore
                if (prevState.length >= 6 && !window.crossbrandNoLimit && !pinnedMetrics.includes(key)) {
                    enqueueSnackbar(t('dashboard.snackbar.max_metrics_limit_reached'), { variant: 'error' });
                    return prevState;
                }
                return [...prevState, key];
            }
        });
    };

    const handleChange = (event: any, newValue: any) => {
        setValue(newValue);
        // We now load the tab data directly when the tab is selected
        if (!loadedTabs.includes(newValue)) {
            setLoadedTabs([...loadedTabs, newValue]);
        }
        // Also reset preloaded pages when changing tabs
        if (newValue === 0) {
            setPreloadedPages([paginationModel.page]);
        }
    };

    useEffect(() => {
        if (data) {
            if (filterStore?.timestep === 'year_week') {
                const yearWeekRegex = /^\d{4}-\d{2}$/;
                const formattedLabels = data.stats.map((item: any) => {
                    return item?.year_week
                });
                setCustomLabels(formattedLabels);
            } else if (filterStore.timestep === 'year_month') {
                const yearWeekRegex = /^\d{4}-\d{2}$/;
                const formatedMontlyDates = data?.stats?.map((item: any) => {
                    if (yearWeekRegex.test(item[filterStore.timestep])) {
                        const [year, week] = item[filterStore.timestep].split('-');
                        return `${week}/${year}`;
                    }
                })
                setCustomLabels(formatedMontlyDates);

            } else {
                const formatedDailyDates = data?.stats?.map((item: any) => {
                    const newDate = new Date(item[filterStore.timestep]);
                    const month = String(newDate.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() is zero-based, so we add 1
                    const day = String(newDate.getUTCDate()).padStart(2, '0');
                    const year = newDate.getUTCFullYear();
                    if (currentLang.value === 'fr') {
                        return `${day}/${month}/${year}`;
                    } else {
                        return `${month}/${day}/${year}`;
                    }
                })
                setCustomLabels(formatedDailyDates);
            }
        }
    }, [currentLang.value, data, filterStore.timestep]);

    // Function to preload next pages and other tabs when browser is idle
    const preloadDataInIdleTime = useCallback(() => {
        // Don't preload if essential data is missing
        if (skipQuery) return;

        const preloadNextPages = () => {
            // Only preload for the keywords tab and if we have data
            if (value === 0 && keywordsData) {
                const nextPage = paginationModel.page + 1;
                const nextNextPage = paginationModel.page + 2;

                // Avoid re-preloading pages we've already loaded
                const pagesToPreload = [nextPage, nextNextPage].filter(p => !preloadedPages.includes(p));

                if (pagesToPreload.length > 0) {
                    // Preload next pages
                    pagesToPreload.forEach(page => {
                        // Use the same API call but with different page
                        // Trigger the query directly without hook
                    });

                    // Update preloaded pages state
                    setPreloadedPages(prev => [...prev, ...pagesToPreload]);
                }
            }
        };

        const preloadOtherTabs = () => {
            // Preload data for tabs that haven't been loaded yet
            const tabsToPreload = [1, 2, 3, 4].filter(tab =>
                !loadedTabs.includes(tab) &&
                // Only preload platforms tab if it's available
                (tab !== 4 || (filterStore?.loadedPlatform?.length >= 2))
            );

            if (tabsToPreload.length > 0) {
                // Update loaded tabs to include preloaded ones
                setLoadedTabs(prev => [...prev, ...tabsToPreload]);
            }
        };

        // Request idle callback to run preloading
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            // @ts-ignore - TypeScript might not recognize requestIdleCallback
            window.requestIdleCallback(() => {
                preloadNextPages();
                preloadOtherTabs();
            }, { timeout: 2000 });
        } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(() => {
                preloadNextPages();
                preloadOtherTabs();
            }, 2000);
        }
    }, [paginationModel, value, keywordsData, filterStore, workspace?.id, skipQuery, preloadedPages, loadedTabs, sortModel]);

    // Reset preloaded pages when pagination model changes
    useEffect(() => {
        setPreloadedPages([paginationModel.page]);
    }, [paginationModel.page, paginationModel.pageSize, filterStore.keywords, filterStore.countries, filterStore.devices, filterStore.platforms]);

    // Trigger preload when component is mounted and after initial data load
    useEffect(() => {
        if (keywordsData && !isFetchingKeywords) {
            preloadDataInIdleTime();
        }
    }, [keywordsData, isFetchingKeywords, preloadDataInIdleTime]);

    // Reset preloaded tabs when filters change
    useEffect(() => {
        setLoadedTabs([value]);
    }, [filterStore.keywords, filterStore.countries, filterStore.devices, filterStore.platforms, value]);

    // Handle opening the stats menu
    const handleStatsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setStatsMenuAnchorEl(event.currentTarget);
    };

    // Handle closing the stats menu
    const handleStatsMenuClose = () => {
        setStatsMenuAnchorEl(null);
        setStatsMenuSearch(''); // Reset search field when menu closes
    };

    const handleRemoveStat = (metricKey: string) => {
        const nextKPIState = kpiToogle.filter((k: string) => k !== metricKey);

        if (nextKPIState.length === 0) {
            enqueueSnackbar(t('dashboard.snackbar.you_cant_deselect_the_last_item'), { variant: 'error' });
            return;
        }

        setKPIToogle(nextKPIState);
        setVisibleStats(prevStats => prevStats.filter(k => k !== metricKey));
    };

    const handleAddStat = (metricKey: string) => {
        const hasReachedLimit = kpiToogle.length >= 5;
        const canBypassLimit = window.crossbrandNoLimit || pinnedMetrics.includes(metricKey);

        if (hasReachedLimit && !canBypassLimit) {
            enqueueSnackbar(t('dashboard.snackbar.max_metrics_limit_reached'), { variant: 'error' });
            return;
        }

        const addToList = (prevStats: string[]) => [...prevStats, metricKey];

        setVisibleStats(addToList);
        setKPIToogle(addToList);
    };

    // Add bypass mechanism
    useEffect(() => {
        // @ts-ignore
        window.enableUnlimitedMetrics = () => {
            // @ts-ignore
            window.crossbrandNoLimit = true;
        };
        // @ts-ignore
        window.disableUnlimitedMetrics = () => {
            // @ts-ignore
            window.crossbrandNoLimit = false;
        };
    }, []);

    // Ajout de la fonction pour gérer le cycle des 3 états
    const handleMetricCheckboxClick = (statKey: string) => {
        const isInVisible = visibleStats.includes(statKey);
        const isInKpi = kpiToogle.includes(statKey);

        if (!isInVisible) {
            // Ajoute à la topbar ET à la chart
            handleAddStat(statKey);
        } else if (isInVisible && isInKpi) {
            // Retire complètement (ni topbar ni chart)
            handleRemoveStat(statKey);
        } else if (isInVisible && !isInKpi) {
            // Si indéterminé, au clic on active la métrique (ajoute à kpiToogle)
            setKPIToogle((prev: string[]) => prev.includes(statKey) ? prev : [...prev, statKey]);
        }
    };

    return (
        <>
            {(!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices)
                ? <SplashScreen />
                : <Box sx={{ px: 3 }}>

                    <div style={{ position: 'relative' }}> {/* Conteneur avec position relative pour le positionnement absolu */}
                        {/* Background for banking grid, matches inactive banking color */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: filterStore?.compare ? 170 : 110,
                                zIndex: -10,
                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.darker.lighter : 'white',
                                borderTopLeftRadius: 20,
                                borderTopRightRadius: 20,
                            }}
                        />
                        <Grid container sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            flexWrap: 'nowrap',
                            width: 'calc(100% - 40px)', // Soustrait la largeur du bouton +
                            '&::-webkit-scrollbar': {
                                height: 6,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.divider,
                                borderRadius: 3,
                            },
                        }}>

                            {visibleStatsData.map((stat, index) => (
                                <Grid item key={index} xs={6} md={4} lg={2} sx={{
                                    flex: { xs: '0 0 50%', sm: '0 0 25%', md: 1 },
                                    minWidth: 200,
                                }}>
                                    {isFetchingStats || (!statsData && !statsError) ? (
                                        <Skeleton
                                            width={'100%'}
                                            height={filterStore?.compare ? '170px' : '110px'}
                                            style={{
                                                borderRadius: 0,
                                                borderTopLeftRadius: index === 0 ? 20 : 0,
                                            }}
                                            animation="wave"
                                        >
                                            <>
                                                <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
                                                <Typography color="success.main">Active</Typography>
                                            </>
                                        </Skeleton>
                                    ) : (
                                        <BankingWidgetSummary
                                            handleKPI={(key: any) => handleToggleKPI(key)}
                                            spanColor={stat.spanColor}
                                            kpiToogle={kpiToogle}
                                            title={stat?.title}
                                            val={stat?.key}
                                            compareData={stat?.compareData}
                                            valueCompare={stat?.valueCompare}
                                            textColor={pinnedMetrics.includes(stat.key) ? stat?.textColor : theme.palette.text.secondary}
                                            icon={stat?.icon}
                                            color={kpiToogle.includes(stat.key) ? stat.color : theme.palette.mode === "dark" ? ColorSchema.DARKER : ColorSchema.DEFAULT}
                                            percent={0}
                                            trendColor={kpiToogle.includes(stat.key) ? stat?.trendColor : '#e2e5e9'}
                                            total={stat?.value}
                                            chart={{
                                                series: kpiToogle.includes(stat.key) ? stat.series : [],
                                            }}
                                            isFirst={index === 0}
                                        />
                                    )}
                                </Grid>
                            ))}

                            {/* Bouton + standard à la fin de la liste */}
                            {allStats.filter(stat => {
                                const menu = stat.key
                                    .replace(/([A-Z])/g, '_$1')
                                    .toLowerCase()
                                    .replace(/^_/, '');
                                const value = statsData?.totals[menu];
                                return value !== 0 && !visibleStats.includes(stat.key);
                            }).length > 0 && (
                                    <Grid item xs={6} md={4} lg={2} sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        flex: { xs: '0 0 50%', sm: '0 0 25%', md: 0.25 },
                                        minWidth: 40,
                                        maxWidth: 40,
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        justifyContent: 'center',
                                        height: '100%',
                                    }}>
                                        {isFetchingStats || (!statsData && !statsError) ? (
                                            <Skeleton
                                                width={'100%'}
                                                height={filterStore?.compare ? '170px' : '110px'}
                                                style={{
                                                    borderRadius: 0,
                                                    borderTopRightRadius: 20,
                                                }}
                                                animation="wave"
                                            >
                                                <>
                                                    <Iconify py="auto" icon="eva:plus-fill" width={24} height={24} />
                                                </>
                                            </Skeleton>
                                        ) :
                                            <Button
                                                onClick={handleStatsMenuOpen}
                                                variant="outlined"
                                                sx={{
                                                    minWidth: '40px',
                                                    width: '40px',
                                                    height: '100%',
                                                    borderRadius: 0,
                                                    borderTopRightRadius: 20,
                                                    p: 0,
                                                    opacity: 1,
                                                    border: "none",
                                                    color: theme.palette.text.secondary,
                                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 43, 54, 0.8)' : 'white',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    ":hover": {
                                                        boxShadow: "none",
                                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 43, 54, 0.8)' : 'white',
                                                    }
                                                }}
                                            >
                                                <Iconify icon="eva:plus-fill" width={24} height={24} />
                                            </Button>
                                        }
                                        <Menu
                                            anchorEl={statsMenuAnchorEl}
                                            open={statsMenuOpen}
                                            onClose={handleStatsMenuClose}
                                            PaperProps={{
                                                sx: {
                                                    mt: 1.5,
                                                    width: 200,
                                                    '& .MuiMenuItem-root': {
                                                        px: 1,
                                                        py: 0.1,
                                                        minHeight: 28,
                                                        typography: 'body2',
                                                        borderRadius: 0.75,
                                                    },
                                                },
                                            }}
                                            MenuListProps={{
                                                autoFocus: false,
                                            }}
                                        >
                                            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder={t('dashboard.global.search') || 'Search...'}
                                                    value={statsMenuSearch}
                                                    onChange={e => setStatsMenuSearch(e.target.value)}
                                                    sx={{ mb: 1, height: 40 }}
                                                    inputProps={{
                                                        style: { fontSize: 15, padding: '10px 12px', height: 40, boxSizing: 'border-box', display: 'flex', alignItems: 'center' },
                                                        ref: searchInputRef,
                                                        autoFocus: true,
                                                    }}
                                                    onKeyDown={e => {
                                                        // Prevent menu from handling navigation keys
                                                        e.stopPropagation();
                                                    }}
                                                />
                                            </Box>
                                            {allStats
                                                .filter(stat => {
                                                    const apiKey = stat.key
                                                        .replace(/([A-Z])/g, '_$1')
                                                        .toLowerCase()
                                                        .replace(/^_/, '');
                                                    const value = statsData?.totals[apiKey];
                                                    // Exclude metrics with no value (but NOT pinnedMetrics)
                                                    return value !== 0;
                                                })
                                                .filter(stat =>
                                                    !statsMenuSearch ||
                                                    stat.title?.toLowerCase().includes(statsMenuSearch.toLowerCase())
                                                )
                                                .map((stat) => {
                                                    const isInVisible = visibleStats.includes(stat.key);
                                                    const isInKpi = kpiToogle.includes(stat.key);
                                                    return (
                                                        <MenuItem
                                                            key={stat.key}
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                handleMetricCheckboxClick(stat.key);
                                                            }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                minHeight: 28,
                                                                py: 0.25,
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Checkbox
                                                                    checked={isInVisible && isInKpi}
                                                                    indeterminate={isInVisible && !isInKpi}
                                                                    sx={{
                                                                        color: isInVisible && !isInKpi ? (theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400]) : undefined,
                                                                        '&.MuiCheckbox-indeterminate': {
                                                                            color: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
                                                                        },
                                                                    }}
                                                                />
                                                                {stat.title}
                                                            </Box>
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Menu>
                                    </Grid>
                                )}
                        </Grid>
                    </div>

                    {isFetchingStats || (!statsData && !statsError) ? (
                        <Skeleton
                            width={'100%'}
                            height={'364px'}
                            style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}
                            animation="wave"
                        >
                            <>
                                <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
                                <Typography color="success.main">Active</Typography>
                            </>
                        </Skeleton>
                    ) : (
                        <ChartLine
                            chart={{
                                series: [
                                    {
                                        categories: customLabels,
                                        data: [
                                            {
                                                name: t('dashboard.brandTraffic.brandSearches'),
                                                data: data?.stats?.map((item: any) => item?.total_organic_searches),
                                                visible: kpiToogle?.includes("brandSearches"),
                                                color: theme.palette[ColorSchema.ERROR].light,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Brand Searches Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.total_organic_searches),
                                                visible: kpiToogle?.includes("brandSearches"),
                                                color: theme.palette[ColorSchema.ERROR].light,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.brandTraffic.totalBrandTraffic'),
                                                data: data?.stats?.map((item: any) => item?.total_organic_clicks + item?.total_ad_clicks),
                                                visible: kpiToogle?.includes("totalBrandTraffic"),
                                                color: theme.palette[ColorSchema.SECONDARY].darker,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Total Brand Traffic Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.total_organic_clicks + item?.total_ad_clicks),
                                                visible: kpiToogle?.includes("totalBrandTraffic"),
                                                color: theme.palette[ColorSchema.SECONDARY].darker,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.brandTraffic.organicBrandTraffic'),
                                                data: data?.stats?.map((item: any) => item?.total_organic_clicks),
                                                visible: kpiToogle?.includes("organicBrandTraffic"),
                                                color: theme.palette[ColorSchema.SUCCESS].light,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Organic Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.total_organic_clicks),
                                                visible: kpiToogle?.includes("organicBrandTraffic"),
                                                color: theme.palette[ColorSchema.SUCCESS].light,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.brandTraffic.adBrandTraffic'),
                                                data: data?.stats?.map((item: any) => item?.total_ad_clicks),
                                                visible: kpiToogle?.includes("adBrandTraffic"),
                                                color: theme.palette[ColorSchema.TERTIARY].light,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Ad Brand Traffic Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.total_ad_clicks),
                                                visible: kpiToogle?.includes("adBrandTraffic"),
                                                color: theme.palette[ColorSchema.TERTIARY].light,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.blended_ctr'),
                                                data: data?.stats?.map((item: any) => item?.blended_ctr),
                                                visible: kpiToogle?.includes("blendedCtr"),
                                                color: theme.palette[ColorSchema.INFO].light,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Blended CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.blended_ctr),
                                                visible: kpiToogle?.includes("blendedCtr"),
                                                color: theme.palette[ColorSchema.INFO].light,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.ad_cost'),
                                                data: data?.stats?.map((item: any) => item?.total_ad_cost),
                                                visible: kpiToogle?.includes("adCost"),
                                                color: theme.palette[ColorSchema.WARNING].light,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Ad Cost Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.total_ad_cost),
                                                visible: kpiToogle?.includes("adCost"),
                                                color: theme.palette[ColorSchema.WARNING].light,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.cpc'),
                                                data: data?.stats?.map((item: any) => {
                                                    const totalAdClicks = item?.total_ad_clicks || 0;
                                                    return totalAdClicks > 0 ? (item?.total_ad_cost || 0) / totalAdClicks : 0;
                                                }),
                                                visible: kpiToogle?.includes("cpc"),
                                                color: "#E02900",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "CPC Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.cpc),
                                                visible: kpiToogle?.includes("CPC"),
                                                color: "#E02900",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.search_brand_traffic'),
                                                data: data?.stats?.map((item: any) => {
                                                    const searchBrandTraffic = item?.search_brand_traffic || 0;
                                                    return searchBrandTraffic;
                                                }),
                                                visible: kpiToogle?.includes("searchBrandTraffic"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: t('dashboard.global.search_brand_traffic_compare'),
                                                data: data?.statsCompare?.map((item: any) => item?.search_brand_traffic),
                                                visible: kpiToogle?.includes("searchBrandTraffic"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.shopping_brand_traffic'),
                                                data: data?.stats?.map((item: any) => {
                                                    const shoppingBrandTraffic = item?.shopping_brand_traffic || 0;
                                                    return shoppingBrandTraffic;
                                                }),
                                                visible: kpiToogle?.includes("shoppingBrandTraffic"),
                                                color: "#64B5F6",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: t('dashboard.global.shopping_brand_traffic_compare'),
                                                data: data?.statsCompare?.map((item: any) => item?.shopping_brand_traffic),
                                                visible: kpiToogle?.includes("shoppingBrandTraffic"),
                                                color: "#64B5F6",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.pmax_brand_traffic'),
                                                data: data?.stats?.map((item: any) => {
                                                    const pmaxBrandTraffic = item?.pmax_brand_traffic || 0;
                                                    return pmaxBrandTraffic;
                                                }),
                                                visible: kpiToogle?.includes("pmaxBrandTraffic"),
                                                color: "#B7DCFB",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Pmax Brand Traffic Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.pmax_brand_traffic),
                                                visible: kpiToogle?.includes("pmaxBrandTraffic"),
                                                color: "#B7DCFB",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.organic_ctr'),
                                                data: data?.stats?.map((item: any) => item?.organic_ctr),
                                                visible: kpiToogle?.includes("organicCtr"),
                                                color: "#085F4A",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Organic CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.organic_ctr),
                                                visible: kpiToogle?.includes("organicCtr"),
                                                color: "#085F4A",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.paid_ctr'),
                                                data: data?.stats?.map((item: any) => item?.paid_ctr),
                                                visible: kpiToogle?.includes("paidCtr"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Paid CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.paid_ctr),
                                                visible: kpiToogle?.includes("paidCtr"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.shopping_paid_ctr'),
                                                data: data?.stats?.map((item: any) => item?.shopping_paid_ctr),
                                                visible: kpiToogle?.includes("shoppingPaidCtr"),
                                                color: "#64B5F6",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Shopping Paid CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.shopping_paid_ctr),
                                                visible: kpiToogle?.includes("shoppingPaidCtr"),
                                                color: "#64B5F6",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.search_paid_ctr'),
                                                data: data?.stats?.map((item: any) => item?.search_paid_ctr),
                                                visible: kpiToogle?.includes("searchPaidCtr"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Search Paid CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.search_paid_ctr),
                                                visible: kpiToogle?.includes("searchPaidCtr"),
                                                color: "#1E88E5",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.pmax_paid_ctr'),
                                                data: data?.stats?.map((item: any) => item?.pmax_paid_ctr),
                                                visible: kpiToogle?.includes("pmaxPaidCtr"),
                                                color: "#B7DCFB",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Pmax Paid CTR Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.pmax_paid_ctr),
                                                visible: kpiToogle?.includes("pmaxPaidCtr"),
                                                color: "#B7DCFB",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.search_ad_cost'),
                                                data: data?.stats?.map((item: any) => item?.search_ad_cost),
                                                visible: kpiToogle?.includes("searchAdCost"),
                                                color: "#FFB300",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Search Ad Cost Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.search_ad_cost),
                                                visible: kpiToogle?.includes("searchAdCost"),
                                                color: "#FFB300",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.shopping_ad_cost'),
                                                data: data?.stats?.map((item: any) => item?.shopping_ad_cost),
                                                visible: kpiToogle?.includes("shoppingAdCost"),
                                                color: "#FFD54F",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Shopping Ad Cost Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.shopping_ad_cost),
                                                visible: kpiToogle?.includes("shoppingAdCost"),
                                                color: "#FFD54F",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.pmax_ad_cost'),
                                                data: data?.stats?.map((item: any) => item?.pmax_ad_cost),
                                                visible: kpiToogle?.includes("pmaxAdCost"),
                                                color: "#FFE180",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Pmax Ad Cost Compare",
                                                data: data?.statsCompare?.map((item: any) => item?.pmax_ad_cost),
                                                visible: kpiToogle?.includes("pmaxAdCost"),
                                                color: "#FFE180",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.search_cpc'),
                                                data: data?.stats?.map((item: any) => {
                                                    const searchCpc = item?.search_cpc || 0;
                                                    return searchCpc;
                                                }),
                                                visible: kpiToogle?.includes("searchCpc"),
                                                color: "#FF5630",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Search CPC Compare",
                                                data: data?.statsCompare?.map((item: any) => {
                                                    const searchCpc = item?.search_cpc || 0;
                                                    return searchCpc;
                                                }),
                                                visible: kpiToogle?.includes("searchCpc"),
                                                color: "#FF5630",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.shopping_cpc'),
                                                data: data?.stats?.map((item: any) => {
                                                    const shoppingCpc = item?.shopping_cpc || 0;
                                                    return shoppingCpc;
                                                }),
                                                visible: kpiToogle?.includes("shoppingCpc"),
                                                color: "#FFAC82",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Shopping CPC Compare",
                                                data: data?.statsCompare?.map((item: any) => {
                                                    const shoppingCpc = item?.shopping_cpc || 0;
                                                    return shoppingCpc;
                                                }),
                                                visible: kpiToogle?.includes("shoppingCpc"),
                                                color: "#FFAC82",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.pmax_cpc'),
                                                data: data?.stats?.map((item: any) => {
                                                    const pmaxCpc = item?.pmax_cpc || 0;
                                                    return pmaxCpc;
                                                }),
                                                visible: kpiToogle?.includes("pmaxCpc"),
                                                color: "#FFAC82",
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Pmax CPC Compare",
                                                data: data?.statsCompare?.map((item: any) => {
                                                    const pmaxCpc = item?.pmax_cpc || 0;
                                                    return pmaxCpc;
                                                }),
                                                visible: kpiToogle?.includes("pmaxCpc"),
                                                color: "#FFAC82",
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                            {
                                                name: t('dashboard.global.blended_cpc'),
                                                data: data?.stats?.map((item: any) => {
                                                    const totalClicks = (item?.total_organic_clicks || 0) + (item?.total_ad_clicks || 0);
                                                    return totalClicks > 0 ? (item?.total_ad_cost || 0) / totalClicks : 0;
                                                }),
                                                visible: kpiToogle?.includes("blendedCpc"),
                                                color: theme.palette.metrics.blendedCpc.main,
                                                yAxis: 1,
                                                dashStyle: "solid"
                                            },
                                            ...(filterStore?.compare === true && filterStore?.timestep === "date" ? [{
                                                name: "Blended CPC Compare",
                                                data: data?.statsCompare?.map((item: any) => {
                                                    const totalClicks = (item?.total_organic_clicks || 0) + (item?.total_ad_clicks || 0);
                                                    return totalClicks > 0 ? (item?.total_ad_cost || 0) / totalClicks : 0;
                                                }),
                                                visible: kpiToogle?.includes("blendedCpc"),
                                                color: theme.palette.metrics.blendedCpc.main,
                                                yAxis: 1,
                                                dashStyle: "ShortDash"
                                            }] : []),
                                        ]
                                    }
                                ],
                            }}
                        />
                    )}
                    {isFetchingStats || (!statsData && !statsError) ? (
                        <Skeleton
                            width={'100%'}
                            height={'700px'}
                            style={{ borderRadius: 10, marginTop: 20 }}
                            animation="wave"
                        >
                            <>
                                <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
                                <Typography color="success.main">Active</Typography>
                            </>
                        </Skeleton>
                    ) : (
                        <Paper
                            sx={{
                                backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
                                boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                                minHeight: 700,
                                borderRadius: 2,
                                marginTop: 3,
                                marginBottom: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 4, pt: 2, mb: 1 }}>
                                <Tabs value={value} onChange={handleChange} aria-label={t('dashboard.brandTraffic.basic_tabs_example')}>
                                    <Tab label={t('dashboard.global.keywords')} />
                                    <Tab label={t('dashboard.global.countries')} />
                                    <Tab label={t('dashboard.global.devices')} />
                                    <Tab label={t('dashboard.global.date')} />
                                    {filterStore?.loadedPlatform?.length >= 2 &&
                                        <Tab label={"Platforms"} />
                                    }
                                </Tabs>
                                <ButtonGroup
                                    size="small"
                                    aria-label="contained primary button group"
                                    sx={{
                                        border: '1px solid rgba(226, 229, 233, 0.16)',
                                        borderRadius: 1,
                                        padding: '3px',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            size: '12px',
                                            pl: 1,
                                            pr: 1,
                                            color: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300]
                                        }}
                                    >
                                        {t('dashboard.global.advanced')}
                                    </Typography>
                                    {[
                                        {
                                            label: (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Icon icon="material-symbols:grid-off-rounded" style={{ fontSize: '14px', color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined }} />
                                                </Box>
                                            ), isActive: !isAdvancedView
                                        },
                                        {
                                            label: (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Icon icon="bi:grid-3x3-gap-fill" style={{ fontSize: '14px', color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined }} />
                                                </Box>
                                            ), isActive: isAdvancedView
                                        }
                                    ].map(({ label, isActive }, index) => (
                                        <Button
                                            key={typeof label === 'string' ? label : 'Advanced'}
                                            variant={isActive ? 'contained' : 'outlined'}
                                            onClick={() => dispatch(setAdvancedView(index === 1))}
                                            sx={{
                                                ...(isActive && {
                                                    border: 0,
                                                    backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                                                    color: (theme) => theme.palette.primary.light,
                                                    borderRadius: 3,
                                                    borderTopRightRadius: index === 0 ? 0 : 3,
                                                    borderBottomRightRadius: index === 0 ? 0 : 3,
                                                    borderTopLeftRadius: index === 1 ? 0 : 3,
                                                    borderBottomLeftRadius: index === 1 ? 0 : 3,
                                                    '&:hover': {
                                                        backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                                                        border: 0,
                                                        boxShadow: 'none'
                                                    }
                                                }),
                                                ...(!isActive && {
                                                    border: 0,
                                                    borderRadius: 3,
                                                    borderTopRightRadius: index === 0 ? 0 : 3,
                                                    borderBottomRightRadius: index === 0 ? 0 : 3,
                                                    borderTopLeftRadius: index === 1 ? 0 : 3,
                                                    borderBottomLeftRadius: index === 1 ? 0 : 3,
                                                    color: (theme) => theme.palette.grey[300],
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                        border: 0,
                                                        boxShadow: 'none',
                                                    }
                                                }),
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Box>

                            <Box >

                                {value === 0 && (
                                    <>
                                        {isFetchingKeywords || (!keywordsData && !keywordsError)
                                            ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Active</Typography>
                                            </Skeleton>
                                            : dataKeywords &&
                                            <Box height={"auto"}>
                                                <DataGridCustomKeywords
                                                    data={dataKeywords}
                                                    paginationModel={paginationModel}
                                                    onPaginationModelChange={handlePaginationModelChange}
                                                    paginationMode="server"
                                                    rowCount={totalRows}
                                                    sortModel={sortModel}
                                                    onSortModelChange={handleSortModelChange}
                                                    filterModel={filterModel && filterModel}
                                                    onFilterModelChange={handleFilterModelChange}
                                                />
                                            </Box>

                                        }
                                    </>
                                )}

                                {value === 1 && (
                                    <>
                                        {!loadedTabs.includes(1) ? (
                                            <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Loading...</Typography>
                                            </Skeleton>
                                        ) : isFetchingCountries || (!countriesData && !countriesError)
                                            ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Active</Typography>
                                            </Skeleton>
                                            : <Box>
                                                <Box height={"auto"}>
                                                    <DataGridCustomCountries data={dataCountries} />
                                                </Box>
                                            </Box>
                                        }
                                    </>
                                )}

                                {value === 2 && (
                                    <>
                                        {!loadedTabs.includes(2) ? (
                                            <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Loading...</Typography>
                                            </Skeleton>
                                        ) : isFetchingDevice || (!devicesData && !deviceError)
                                            ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Active</Typography>
                                            </Skeleton>
                                            : <>
                                                <Box height={"auto"}>
                                                    <DataGridCustomDevices data={dataDevices.filter((item: any) => item.device !== "Na")} />
                                                </Box>
                                            </>
                                        }
                                    </>
                                )}

                                {value === 3 && (
                                    <>
                                        {!loadedTabs.includes(3) ? (
                                            <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Loading...</Typography>
                                            </Skeleton>
                                        ) : isFetchingStatsDay || (!dataDay && !statsDayError)
                                            ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Active</Typography>
                                            </Skeleton>
                                            : <>
                                                <Box height={"auto"}>
                                                    <DataGridCustomDay data={dataDay} />
                                                </Box>
                                            </>
                                        }
                                    </>
                                )}

                                {filterStore?.loadedPlatform?.length >= 2 && value === 4 && (
                                    <>
                                        {!loadedTabs.includes(4) ? (
                                            <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Loading...</Typography>
                                            </Skeleton>
                                        ) : isFetchingPlatformsData || (!platformsData && !platformsDataError)
                                            ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                                <Typography color={"success.main"}>Active</Typography>
                                            </Skeleton>
                                            : <>
                                                <Box height={"auto"}>
                                                    <DataGridCustomPlatforms data={dataPlatforms} />
                                                </Box>
                                            </>
                                        }
                                    </>
                                )}

                            </Box>
                        </Paper>
                    )}
                </Box>
            }
        </>
    )
}
